import React, { useState, useCallback, useEffect, useRef, useMemo, memo } from "react";
import { OrangeCap } from "./NormalSearch";
import SuggestionsDropdown from "./SuggestionsDropdown";
import useClickOutside from "./hooks/useClickOutside";
import useDebouncedValue from "./hooks/useDebouncedValue";
import {
    getManualSuggestions,
    getRecentLocationSearches,
    pushRecentLocationSearch,
} from "./searchSuggestions";
import { SearchIcon } from "./icons";

const DEBOUNCE_MS = 350;

const PLACEHOLDER = "Enter location to search manually";
const ARIA_LABEL = "Search properties by location";

/**
 * The homepage search bar: manual location search only.
 *
 * The AI-advisor mode (and with it the mode selector dropdown that used to
 * switch between the two) was removed from this bar -- the AI experience is
 * now entered from the floating "Talk to Me" pill
 * (components/TalkToMeWidget.jsx), which hands off to the dedicated
 * /ai-search page. The /ai-search route and everything behind it are
 * untouched; only this bar's AI branch is gone, so the homepage has exactly
 * one search behaviour and needs no mode picker.
 *
 * Behaviour preserved verbatim from the manual mode: live-filtering with a
 * 350ms debounce back to the parent, the shared suggestions dropdown
 * (recent searches when empty, matching locations while typing), keyboard
 * navigation, clear, and the explicit search button.
 */
function UnifiedSearch({ onSearch }) {
    const [text, setText] = useState("");
    const [recentSearches, setRecentSearches] = useState(getRecentLocationSearches);

    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Stable id so the input can point aria-activedescendant/aria-controls
    // at the listbox and its options without colliding if multiple search
    // bars are ever rendered on the same page.
    const [listboxId] = useState(
        () => `search-suggestions-${Math.random().toString(36).slice(2, 9)}`
    );

    const outerRef = useRef(null);
    const suggestionsRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    // The live-filter effect below runs once on mount with an empty query,
    // which would fire onSearch(null) and make the parent re-fetch the
    // listing it already loads itself. Skipped for that first empty run
    // only -- clearing the box later still reports null, as before.
    const didRunInitialFilterRef = useRef(false);

    const debouncedText = useDebouncedValue(text, DEBOUNCE_MS);

    // Cancels any in-flight search request and starts tracking a new one;
    // the returned signal is passed through to onSearch so a parent-side
    // fetch can be aborted if the person keeps typing.
    const nextSearchSignal = useCallback(() => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller.signal;
    }, []);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    const suggestions = useMemo(
        () => getManualSuggestions(text, recentSearches),
        [text, recentSearches]
    );

    const suggestionsHeading = useMemo(
        () => (text.trim() ? null : "Recent searches"),
        [text]
    );

    const activeOptionId =
        highlightedIndex >= 0 && suggestions[highlightedIndex]
            ? `${listboxId}-opt-${suggestions[highlightedIndex].id}`
            : undefined;

    const closeAllDropdowns = useCallback(() => {
        setSuggestionsOpen(false);
    }, []);
    useClickOutside(outerRef, closeAllDropdowns, suggestionsOpen);

    // Live-filters as you type.
    useEffect(() => {
        const trimmed = debouncedText.trim();
        if (!didRunInitialFilterRef.current) {
            didRunInitialFilterRef.current = true;
            if (!trimmed) return;
        }
        const signal = nextSearchSignal();
        onSearch?.(trimmed || null, { signal });
    }, [debouncedText, onSearch, nextSearchSignal]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [suggestions]);

    useEffect(() => () => clearTimeout(blurTimerRef.current), []);

    const runManualSearch = useCallback((query) => {
        const q = query.trim();
        if (!q) return;
        setSuggestionsOpen(false);
        setRecentSearches(pushRecentLocationSearch(q));
        const signal = nextSearchSignal();
        onSearch?.(q, { signal });
    }, [onSearch, nextSearchSignal]);

    const handleSearch = useCallback(() => {
        runManualSearch(text);
    }, [text, runManualSearch]);

    const handleSelectSuggestion = useCallback((item) => {
        setText(item.label);
        inputRef.current?.focus();
        runManualSearch(item.label);
    }, [runManualSearch]);

    const handleClear = useCallback(() => {
        setText("");
        const signal = nextSearchSignal();
        onSearch?.(null, { signal });
        inputRef.current?.focus();
    }, [onSearch, nextSearchSignal]);

    const handleInputFocus = useCallback(() => {
        clearTimeout(blurTimerRef.current);
        setSuggestionsOpen(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        // small delay so a click on a suggestion registers before the list unmounts
        blurTimerRef.current = setTimeout(() => setSuggestionsOpen(false), 120);
    }, []);

    const handleInputKeyDown = useCallback((e) => {
        if (!suggestionsOpen || suggestions.length === 0) {
            if (e.key === "Enter") handleSearch();
            return;
        }
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % suggestions.length);
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
                break;
            case "Enter":
                if (highlightedIndex >= 0) {
                    e.preventDefault();
                    handleSelectSuggestion(suggestions[highlightedIndex]);
                } else {
                    handleSearch();
                }
                break;
            case "Escape":
                setSuggestionsOpen(false);
                break;
            default:
                break;
        }
    }, [suggestionsOpen, suggestions, highlightedIndex, handleSearch, handleSelectSuggestion]);

    return (
        <div className="search-pill-outer" ref={outerRef}>
            <div className="search-pill unified">
                <div className="pill-content">
                    <div className="input-wrapper">
                        <input
                            ref={inputRef}
                            className="pill-input"
                            placeholder={PLACEHOLDER}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            autoComplete="off"
                            aria-label={ARIA_LABEL}
                            role="combobox"
                            aria-expanded={suggestionsOpen}
                            aria-autocomplete="list"
                            aria-controls={suggestionsOpen ? listboxId : undefined}
                            aria-activedescendant={suggestionsOpen ? activeOptionId : undefined}
                        />
                        {text && (
                            <button className="clear-btn" onClick={handleClear} aria-label="Clear" type="button">
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <OrangeCap height={58}>
                    <button className="pill-btn" onClick={handleSearch} type="button" aria-label="Search">
                        <SearchIcon />
                    </button>
                </OrangeCap>
            </div>

            {suggestionsOpen && (
                <SuggestionsDropdown
                    listboxId={listboxId}
                    items={suggestions}
                    highlightedIndex={highlightedIndex}
                    onHover={setHighlightedIndex}
                    onSelect={handleSelectSuggestion}
                    heading={suggestionsHeading}
                    dropdownRef={suggestionsRef}
                />
            )}
        </div>
    );
}

export default memo(UnifiedSearch);
